import { Link } from "react-router";
import { downloadCsv, errorMessage } from "../api/client.ts";
import type { StatementData, StatementItem } from "../api/types.ts";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { DateRange, useUrlFilters } from "../components/Filters.tsx";
import { IconDownload } from "../components/Icons.tsx";
import { Pager } from "../components/Pager.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, ChainLogo, EmptyState, ExternalLink, Hash, PageHeader, Time } from "../components/ui.tsx";
import { fmt, tr, useT } from "../i18n/index.tsx";
import { useAssetList } from "../lib/assets.ts";
import { chainName, exactUnits, formatUnits, shorten, tokenOf } from "../lib/format.ts";
import { usePagedList } from "../lib/paging.ts";

const isNeg = (a: string) => a.startsWith("-");
const abs = (a: string) => a.replace(/^-/, "");
/** Row description by entry kind; unknown kinds keep the server's text. */
const describe = (r: StatementItem): string => (tr().statement.kinds as Record<string, string>)[r.kind] ?? r.description;

/** Bank-statement view of one asset's available balance: Time | Description | Reference | In | Out | Balance. */
export function Statement() {
  const t = useT();
  const { values, set, setMany } = useUrlFilters(["asset", "from", "to"]);
  const { assets, loaded } = useAssetList();
  const toast = useToast();
  const asset = values.asset || assets[0]?.asset || "";
  const params = { asset, from: values.from, to: values.to };
  const list = usePagedList<StatementItem, StatementData>(asset ? "/statement" : "", params);
  const head = list.data;
  const decimals = head?.decimals ?? assets.find((a) => a.asset === asset)?.decimals ?? 6;
  const token = tokenOf(asset);
  const info = assets.find((a) => a.asset === asset);

  // Totals of the rows on this page (BigInt, smallest units).
  const sum = (pick: (a: string) => boolean) => list.items.reduce((t, r) => (pick(r.amount) ? t + BigInt(abs(r.amount)) : t), 0n);
  const totalIn = sum((a) => !isNeg(a) && a !== "0");
  const totalOut = sum(isNeg);

  const cols: Column<StatementItem>[] = [
    { key: "time", header: t.statement.time, cell: (r) => <Time iso={r.created_at} />, mobile: "title" },
    { key: "desc", header: t.statement.description, cell: describe },
    {
      key: "ref", header: t.statement.reference, cell: (r) => r.ref_id && (r.ref_type === "deposit" || r.ref_type === "payout")
        ? (
          <Link to={`/${r.ref_type === "deposit" ? "deposits" : "payouts"}?open=${encodeURIComponent(r.ref_id)}`} className="ref-link" title={r.ref_id}>
            {r.ref_type === "deposit" ? t.statement.refDeposit : t.statement.refPayout} <span className="mono-sm">{shorten(r.ref_id, 8, 4)}</span>
          </Link>
        )
        : r.transfer
          ? (
            <span className="ref-transfer">
              <span className="muted small">{r.transfer.direction === "out" ? t.statement.refTo : t.statement.refFrom}</span>
              <Hash value={r.transfer.counterparty} />
              {r.transfer.explorer_url && <ExternalLink href={r.transfer.explorer_url}>{t.statement.refTx}</ExternalLink>}
            </span>
          )
          : <span className="muted">–</span>,
    },
    { key: "in", header: t.statement.in, align: "right", cell: (r) => (!isNeg(r.amount) && r.amount !== "0" ? <span className="amount" title={exactUnits(r.amount, decimals)}>{formatUnits(r.amount, decimals)}</span> : null) },
    { key: "out", header: t.statement.out, align: "right", cell: (r) => (isNeg(r.amount) ? <span className="amount" title={exactUnits(abs(r.amount), decimals)}>{formatUnits(abs(r.amount), decimals)}</span> : null) },
    { key: "bal", header: t.statement.balance, align: "right", cell: (r) => <strong className="amount" title={exactUnits(r.balance_after, decimals)}>{formatUnits(r.balance_after, decimals)}</strong> },
  ];

  return (
    <div className="page">
      <PageHeader
        title={t.statement.title}
        actions={
          asset && (
            <button type="button" className="btn btn-ghost"
              onClick={() => downloadCsv("/statement/export.csv", params, `statement-${asset.replace(":", "-")}-${new Date().toISOString().slice(0, 10)}.csv`).catch((e) => toast(errorMessage(e), "error"))}>
              <IconDownload size={18} /> {t.common.exportCsv}
            </button>
          )
        }
      >
        {t.statement.intro}
      </PageHeader>

      {loaded && !assets.length ? (
        <EmptyState title={t.statement.noNetworksTitle} action={<Link to="/vault" className="btn btn-primary">{t.statement.noNetworksAction}</Link>}>
          {t.statement.noNetworksBody}
        </EmptyState>
      ) : (<>
      <div className="statement-head card">
        <div className="filters statement-filters">
          <div className="select-wrap with-logo">
            {info && <ChainLogo chain={info.chain} size={20} />}
            <label htmlFor="st-asset" className="sr-only">{t.common.asset}</label>
            <select id="st-asset" value={asset} onChange={(e) => set("asset", e.target.value)}>
              {!assets.length && <option value="">{t.statement.loadingOption}</option>}
              {assets.map((a) => <option key={a.asset} value={a.asset}>{a.token} · {chainName(a.chain)}</option>)}
            </select>
          </div>
        </div>
        <div className="statement-balance">
          <div className="eyebrow">{t.statement.availableNow}</div>
          <div className="summary-value">{head ? formatUnits(head.balance, head.decimals) : "–"} <span className="unit">{token}</span></div>
        </div>
      </div>
      <DateRange from={values.from ?? ""} to={values.to ?? ""} onChange={(from, to) => setMany({ from, to })} />

      {list.error ? (
        <Alert tone="error">{errorMessage(list.error)}</Alert>
      ) : (
        <DataTable
          caption={fmt(t.statement.caption, { token })}
          className="statement-table"
          columns={cols}
          rows={list.items}
          rowKey={(r) => r.id}
          loading={list.loading}
          empty={<EmptyState title={t.statement.emptyTitle}>{t.statement.emptyBody}</EmptyState>}
          footRow={{
            time: <span className="muted">{t.statement.thisPage}</span>,
            in: <span className="amount" title={exactUnits(totalIn.toString(), decimals)}>{formatUnits(totalIn.toString(), decimals)}</span>,
            out: <span className="amount" title={exactUnits(totalOut.toString(), decimals)}>{formatUnits(totalOut.toString(), decimals)}</span>,
          }}
          footer={<Pager page={list.page} limit={list.limit} count={list.items.length} total={list.total} hasMore={list.hasMore} onPage={list.setPage} onLimit={list.setLimit} />}
        />
      )}
      </>)}
    </div>
  );
}
